import { useState, useRef, useEffect } from 'react';
import { RichTextDisplay } from '@/components/ui/rich-text-display';
import { Button } from '@/components/ui/button';
import { MessageSquare } from 'lucide-react';
import { getTextSelection, type TextSelection } from '@/lib/comment-utils';

interface CommentAnnotatorProps {
  content: string;
  onSelectForComment: (selection: TextSelection) => void;
}

export function CommentAnnotator({ content, onSelectForComment }: CommentAnnotatorProps) {
  const [showButton, setShowButton] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<TextSelection | null>(null);
  const [buttonPos, setButtonPos] = useState({ top: 0, left: 0 });
  const contentRef = useRef<HTMLDivElement>(null);

  const handleMouseUp = () => {
    setTimeout(() => {
      const selection = window.getSelection();
      if (!selection || selection.toString().trim().length === 0) {
        setShowButton(false);
        return;
      }
      if (!contentRef.current?.contains(selection.anchorNode)) {
        setShowButton(false);
        return;
      }
      const textSelection = getTextSelection();
      if (!textSelection?.text.trim()) {
        setShowButton(false);
        return;
      }
      setPendingSelection(textSelection);

      if (selection.rangeCount > 0) {
        const rect = selection.getRangeAt(0).getBoundingClientRect();
        const spacing = 10;
        let top = rect.bottom + spacing;
        let left = rect.left + rect.width / 2;
        if (top + 36 > window.innerHeight) top = rect.top - 36 - spacing;
        left = Math.max(10, Math.min(left, window.innerWidth - 80));
        if (top < 0) top = rect.bottom + spacing;
        setButtonPos({ top, left });
      }
      setShowButton(true);
    }, 100);
  };

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.addEventListener('mouseup', handleMouseUp);
    return () => el.removeEventListener('mouseup', handleMouseUp);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        !contentRef.current?.contains(e.target as Node) &&
        !(e.target as HTMLElement).closest('.comment-float-btn')
      ) {
        setShowButton(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCommentClick = () => {
    if (!pendingSelection) return;
    onSelectForComment(pendingSelection);
    setShowButton(false);
    setPendingSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  return (
    <div className="relative" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
      <div ref={contentRef} className="select-text" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
        <RichTextDisplay content={content} />
      </div>

      {showButton && (
        <div
          className="fixed comment-float-btn z-50"
          style={{ top: buttonPos.top, left: buttonPos.left, transform: 'translateX(-50%)' }}
        >
          <Button size="sm" onClick={handleCommentClick} className="shadow-lg gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" />
            Comment
          </Button>
        </div>
      )}
    </div>
  );
}
